import React, {useState} from 'react'
import { Check } from 'lucide-react'
import {useNavigate, useParams} from 'react-router-dom'
import {useIntl} from 'react-intl'
import {useVerifyEmail} from '../../../../hooks/queries'
import {readFirstFactor} from '../../../../lib/twoFactor/firstFactor'
import {useFirstFactorRouter} from '../../../../hooks/useFirstFactorRouter'

export default function VerifyEmail () {
  const [isError, setIsError] = useState(false)
  const [isVerify, setIsVerify] = useState(false)
  const [status, setStatus] = useState('')
  const intl = useIntl()

  const params = useParams()
  const {uid, token} = params
  const navigate = useNavigate()
  const routeFirstFactor = useFirstFactorRouter()

  // Use React Query verify email mutation
  const verifyEmailMutation = useVerifyEmail()

  /**
   * Verifies the user's email using React Query mutation
   *
   * @async
   * @function verify
   * @returns {Promise<void>}
   */
  async function verify() {
    try {
      const response = await verifyEmailMutation.mutateAsync({
        token: `${uid}/${token}`,
      })

      // Activation can answer with the second-factor SETUP challenge (no
      // cookies): the account must enrol before it may sign in.
      const outcome = readFirstFactor(response)
      if (outcome.kind !== 'session') {
        routeFirstFactor(outcome, () => undefined)
        return
      }

      setStatus('Your email has been successfully verified')
      setIsError(false)
      setIsVerify(true)
      setTimeout(() => {
        navigate('/auth/login')
      }, 5000)
    } catch (e: any) {
      setIsError(true)
      setStatus(e?.response?.data?.detail || e?.message || 'Unfortunately, there was a problem verifying your email')
    }
  }

  return (
    <section className='bg-red h-100'>
      <div className='position-relative d-flex justify-content-center align-items-center px-4 px-md-5 py-5 h-100 text-center text-lg-start app-navbar gradient'>
        <div className='container'>
          <div className='justify-content-between align-items-center row gx-lg-5'>
            <div className='mb-5 mb-lg-0 col-lg-6'>
              <h1
                className='my-5 display-3 fw-bold ls-tight'
                style={{textAlign: 'justify'}}
              >
                {intl.formatMessage({id: 'Netbaan'})}
                <br />
                <span className='text-primary'>{intl.formatMessage({id: 'Cloud Platform'})}</span>
              </h1>
              <p style={{color: 'hsl(217, 10%, 50.8%)', textAlign: 'justify'}}>
                {intl.formatMessage({
                  id: 'Secure your infrastructure with our cutting-edge technologies',
                })}
              </p>
            </div>

            {!isVerify ? (
              <div className='mb-lg-0 py-10 col-lg-6' style={{maxWidth: 500}}>
                <div
                  hidden={!status || !isError}
                  className={`mb-lg-15 h-40px alert ${
                    isError ? 'alert-danger' : 'alert-success'
                  }  rounded-pill`}
                >
                  <div className='font-weight-bold alert-text'>{status}</div>
                </div>

                <div className='d-flex flex-column justify-content-center align-items-center gap-10 py-10 p-5 text-center card'>
                  <h1 className=''>
                    {intl.formatMessage({id: 'Verify your email to finish signing up'})}
                  </h1>

                  <h4 className=''>
                    {intl.formatMessage({id: 'Thank you for choosing'})}{' '}
                    <strong>{intl.formatMessage({id: 'NetBaan Sharif'})} </strong>.
                  </h4>

                  <button
                    className='btn btn-success'
                    style={{opacity: 0.9}}
                    disabled={verifyEmailMutation.isPending}
                    onClick={verify}
                  >
                    {!verifyEmailMutation.isPending && (
                      <span className='indicator-label fw-bold'>
                        {intl.formatMessage({id: 'Verify'})}
                      </span>
                    )}
                    {verifyEmailMutation.isPending && (
                      <span className='indicator-progress' style={{display: 'block'}}>
                        {intl.formatMessage({id: 'Please wait'})}
                        ...
                        <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                      </span>
                    )}
                  </button>
                </div>
              </div>
            ) : (
              <div className='d-flex flex-column justify-content-center align-items-center gap-5 mb-lg-0 py-10 py-10 text-center card col-lg-6'>
                <Check size={56} className='svg-icon-6x svg-icon-success' />
                <h1 className='text-success'>
                  {intl.formatMessage({id: 'Email verification was successful'})}
                </h1>
                <p>{intl.formatMessage({id: 'You can login with your email'})}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  )
}
